import { HandWaving, CalendarDots, CreditCard, Target, CheckCircle, FileText, ListChecks } from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { serviceSaveGrossPrice } from "@/lib/service-price-save";
import { isMatchingProfileReady } from "@/lib/matching/profile-readiness";
import { OnboardingShell, type StepMeta } from "@/components/onboarding/OnboardingShell";
import { WelcomeStep } from "@/components/onboarding/steps/WelcomeStep";
import { AgreementStep } from "@/components/onboarding/steps/AgreementStep";
import { ReviewStep } from "@/components/onboarding/steps/ReviewStep";
import { AvailabilityStep } from "@/components/onboarding/steps/AvailabilityStep";
import { ServicesStep } from "@/components/onboarding/steps/ServicesStep";
import { MatchingStep } from "@/components/onboarding/steps/MatchingStep";
import { FinishStep } from "@/components/onboarding/steps/FinishStep";
import { applyServiceTemplate, canOpenOnboardingStep, summarizeDays } from "@/components/onboarding/onboarding-logic";
import { useOnboardingWizard } from "@/components/onboarding/use-onboarding-wizard";
import type { StepKey } from "@/components/onboarding/types";

export const Route = createFileRoute("/dashboard/therapist/onboarding")({
  component: OnboardingPage,
});

const icon = (I: typeof HandWaving) => <I weight="duotone" className="size-[17px]" />;

const STEP_INFO: Record<StepKey, { short: string; sub: string; title: string; deck: string; icon: React.ReactNode }> = {
  welcome:      { short: "Dobrodošli", sub: "Kratak uvod", title: "Dobrodošli", deck: "Tvoja prijava je odobrena. Podesimo ono što profil čini aktivnim i vidljivim klijentima.", icon: icon(HandWaving) },
  agreement:    { short: "Ugovor", sub: "Pročitaj i potvrdi", title: "Ugovor o saradnji", deck: "Pročitaj dokumente i potvrdi ih na dnu stranice. Bez ovoga profil ne može biti aktivan.", icon: icon(FileText) },
  review:       { short: "Provjera profila", sub: "Preneseni podaci", title: "Provjeri svoj profil", deck: "Ovo smo prenijeli sa stare platforme. Ako nešto nije tačno, klikni Izmijeni.", icon: icon(ListChecks) },
  availability: { short: "Radno vrijeme", sub: "Kada primaš klijente", title: "Kada radiš?", deck: "Označi dane i sate kada primaš klijente. Klijenti vide samo slobodne termine u tom vremenu.", icon: icon(CalendarDots) },
  services:     { short: "Usluga", sub: "Šta nudiš", title: "Tvoja prva usluga", deck: "Usluga je ono što klijent rezerviše. Prijedlog je već popunjen, promijeni samo ono što treba.", icon: icon(CreditCard) },
  matching:     { short: "Oblasti rada", sub: "Da te klijenti nađu", title: "Sa čime najviše pomažeš?", deck: "Na osnovu ovoga te preporučujemo klijentima koji traže baš tvoju pomoć. Označi sve što važi za tebe.", icon: icon(Target) },
  finish:       { short: "Gotovo", sub: "Pregled i kraj", title: "Sve je spremno", deck: "Klikni Završi i tvoj profil postaje aktivan. Sve možeš mijenjati u Postavkama profila.", icon: icon(CheckCircle) },
};

function OnboardingPage() {
  const {
    index, stepKey, STEP_ORDER, completion, saving, requiredDone, goTo, handlePrimary,
    isMigrated, showAgreement, showContract, showBillingTerms,
    contractQuery, contractPending, termsPending,
    agreedToContract, setAgreedToContract, termsAccepted, agreedToTerms, setAgreedToTerms,
    isVolunteer, payoutMethod, setPayoutMethod, billing, setBilling,
    days, setDays, booking, setBooking, officeAddress, setOfficeAddress,
    service, setService, selectedTemplate, setSelectedTemplate, otherServiceCount, serviceSavedBaseline,
    pricingConfig, matching, setMatching,
    availabilityEdit, setAvailabilityEdit, serviceEdit, setServiceEdit,
  } = useOnboardingWizard();

  const info =
    stepKey === "welcome" && isMigrated
      ? { ...STEP_INFO.welcome, deck: "Prešli smo na novu platformu. Potvrdi podatke za isplatu, prihvati ugovor i provjeri preneseni profil." }
      : stepKey === "agreement" && showBillingTerms
        ? { ...STEP_INFO.agreement, title: "Ugovor i podaci za isplatu" }
        : STEP_INFO[stepKey];

  const priceConfig = pricingConfig.config;
  const availabilityForm = <AvailabilityStep days={days} onDaysChange={setDays} booking={booking} onBookingChange={setBooking} />;
  const serviceForm = (
    <ServicesStep
      service={service}
      onServiceChange={(s) => { setService(s); setSelectedTemplate(null); }}
      selectedTemplate={selectedTemplate}
      onSelectTemplate={(k) => { setSelectedTemplate(k); setService((prev) => ({ ...applyServiceTemplate(k), id: prev.id })); }}
      savedBaseline={serviceSavedBaseline}
      officeAddress={officeAddress}
      onOfficeAddressChange={setOfficeAddress}
    />
  );

  function renderStep() {
    switch (stepKey) {
      case "welcome": return <WelcomeStep migrated={isMigrated} showAgreement={showAgreement} />;
      case "agreement": return (
        <AgreementStep
          contract={showContract ? { data: contractQuery.data, isLoading: contractQuery.isLoading, agreed: agreedToContract, onAgreedChange: setAgreedToContract } : undefined}
          billingTerms={showBillingTerms ? { accepted: termsAccepted, payoutMethod, onPayoutMethodChange: setPayoutMethod, billing, onBillingChange: setBilling, isVolunteer, agreed: agreedToTerms, onAgreedChange: setAgreedToTerms } : undefined}
        />
      );
      case "review": return (
        <ReviewStep
          days={days}
          service={service}
          clientPrice={priceConfig && service.price ? serviceSaveGrossPrice(service.price, serviceSavedBaseline, priceConfig) : null}
          otherServiceCount={otherServiceCount}
          editingAvailability={availabilityEdit !== null}
          onToggleAvailability={() => {
            if (availabilityEdit) { setDays(availabilityEdit.days); setBooking(availabilityEdit.booking); setAvailabilityEdit(null); }
            else setAvailabilityEdit({ days, booking });
          }}
          availabilityForm={availabilityForm}
          editingService={serviceEdit !== null}
          onToggleService={() => {
            if (serviceEdit) { setService(serviceEdit.service); setOfficeAddress(serviceEdit.officeAddress); setServiceEdit(null); }
            else setServiceEdit({ service, officeAddress });
          }}
          serviceForm={serviceForm}
        />
      );
      case "availability": return availabilityForm;
      case "services": return serviceForm;
      case "matching": return <MatchingStep value={matching} onChange={setMatching} />;
      case "finish": return (
        <FinishStep
          availabilitySummary={summarizeDays(days).join("; ") || "Nije postavljeno"}
          serviceSummary={service.name ? `${service.name}, ${service.durationMinutes} min` : "Nije postavljeno"}
          matchingSummary={`Glavne oblasti: ${matching.primaryProblemTags.length}, grupe klijenata: ${matching.populationTags.length}`}
        />
      );
    }
  }

  const steps: StepMeta[] = STEP_ORDER.map((k) => ({ key: k, title: STEP_INFO[k].short, sub: STEP_INFO[k].sub, icon: STEP_INFO[k].icon }));
  const reviewEditing = availabilityEdit !== null || serviceEdit !== null;
  const primaryLabel =
    stepKey === "finish" ? "Završi"
    : stepKey === "welcome" ? "Počnimo"
    : stepKey === "agreement" ? "Prihvatam i nastavljam"
    : stepKey === "review" && !reviewEditing ? "Sve je tačno, nastavi"
    : "Sačuvaj i nastavi";

  return (
    <OnboardingShell
      steps={steps}
      activeIndex={index}
      completion={completion}
      onJump={goTo}
      canJumpTo={(targetIndex) => canOpenOnboardingStep(STEP_ORDER[targetIndex]!, completion, STEP_ORDER)}
      title={info.title}
      deck={info.deck}
      onBack={() => goTo(index - 1)}
      onPrimary={handlePrimary}
      primaryLabel={primaryLabel}
      primaryDisabled={
        (stepKey === "finish" && !requiredDone) ||
        (stepKey === "agreement" && ((contractPending && !agreedToContract) || (termsPending && !agreedToTerms))) ||
        (stepKey === "services" && !priceConfig) ||
        (stepKey === "review" && serviceEdit !== null && !priceConfig) ||
        (stepKey === "matching" && !isMatchingProfileReady(matching))
      }
      saving={saving}
      navigationDisabled={saving}
      showBack={index > 0}
    >
      {renderStep()}
    </OnboardingShell>
  );
}
